/**
 * Blocco dello scroll del body che conserva la posizione.
 *
 * IB-656 (Matteo, 18/08). Aprendo il menu su mobile il tema applica
 * `position: fixed` al body tramite la classe `overflow-hidden`, ma senza
 * memorizzare la posizione di scroll. Con position: fixed e nessun `top` il
 * documento viene ridisegnato dall'inizio: alla chiusura l'utente si ritrova
 * in cima alla pagina invece che dove era, e mentre il menu e' aperto risulta
 * difficile orientarsi.
 *
 * Verificato sul tema pre-prod, viewport iPhone 13: con la pagina a 850px, ad
 * apertura menu il body diventa fixed con `top` vuoto e alla chiusura la
 * posizione e' 0.
 *
 * Perche' solo `overflow-hidden`: gli altri overlay del tema gestiscono gia'
 * l'offset da soli, e intervenire anche su quelli farebbe sovrascrivere
 * `body.style.top` a vicenda.
 *   - `fixed` -> modal.js, main.js (righe 372, 490, 695): salvano scrollY
 *   - `pdp-lightbox-open` -> pdp-image-lightbox.js: salva this.scrollY
 * Escluso anche `active-filter-mobile`: su mobile il pannello filtri e' inline
 * nel flusso della pagina e non ha uno scroll proprio, quindi e' il documento
 * a dover scorrere per mostrarlo. Bloccarlo impedirebbe di scorrere i filtri.
 */
(() => {
  const LOCK_CLASSES = ['overflow-hidden'];
  let lockedAt = null;

  /**
   * Ultima posizione nota a body non bloccato.
   *
   * Non si puo' leggere window.scrollY al momento del lock: la callback del
   * MutationObserver arriva dopo che il browser ha applicato position: fixed,
   * quando scrollY vale gia' 0. Va quindi tenuta aggiornata durante lo scroll.
   */
  let ultimaY = window.scrollY || 0;
  window.addEventListener(
    'scroll',
    () => {
      if (lockedAt === null) ultimaY = window.scrollY || 0;
    },
    { passive: true }
  );

  function isLocked() {
    return LOCK_CLASSES.some((c) => document.body.classList.contains(c));
  }

  function lock() {
    if (lockedAt !== null) return;
    // Se un altro componente sta gia' gestendo l'offset, non interferire.
    if (document.body.style.top) return;
    lockedAt = ultimaY;
    document.body.style.top = `-${lockedAt}px`;
    compensaHeader();
  }

  /**
   * IB-656 (feedback Eliana, 27/08). Con il body parcheggiato a -Y l'header
   * resta fuori dallo schermo e sopra il menu si vede il contenuto di pagina
   * che stava a quell'offset: il tema non ha header sticky (le sezioni sono
   * in flusso, position: relative). Si contro-trasla il gruppo header dello
   * stesso offset, cosi' torna al suo posto nel viewport mantenendo intatto
   * lo stack announcement / header / promo strip.
   *
   * La contro-traslazione usa `top` sulle sezioni gia' `position: relative`,
   * non `transform`: un transform farebbe da containing block ai pannelli
   * `position: fixed` del menu, che si posizionerebbero rispetto alla sezione
   * header (31px sotto l'announcement) invece che al viewport.
   */
  function compensaHeader() {
    if (!lockedAt) return;
    document.body.style.setProperty('--scroll-lock-offset', `${lockedAt}px`);
    document.body.classList.add('is-scroll-locked-offset');
    aggiornaHeaderEnd();
  }

  function ripristinaHeader() {
    document.body.style.removeProperty('--scroll-lock-offset');
    document.body.classList.remove('is-scroll-locked-offset');
    aggiornaHeaderEnd();
  }

  /**
   * IB-656 (verifica pre-prod 14/09). Il toggle dell'hamburger calcola
   * --header-end al click, quando la pagina e' ancora scrollata: con scrollY
   * 800 vale circa -650px e il pannello del menu, fixed a quella quota, copre
   * l'header appena riportato in vista. Si ricalcola dopo la contro-traslazione
   * (e dopo il ripristino), leggendo la posizione reale dell'header.
   */
  function aggiornaHeaderEnd() {
    const header = document.querySelector('store-header');
    if (header && typeof header.setHeaderEnd === 'function') header.setHeaderEnd();
  }

  function unlock() {
    if (lockedAt === null) return;
    const y = lockedAt;
    lockedAt = null;
    document.body.style.top = '';
    // Ripristino immediato: con lo smooth scroll si vedrebbe il salto.
    const root = document.documentElement;
    const precedente = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, y);
    root.style.scrollBehavior = precedente;
    // Dopo lo scrollTo, cosi' --header-end si misura sulla posizione reale.
    ripristinaHeader();
  }

  function sync() {
    if (isLocked()) lock();
    else unlock();
  }

  function avvia() {
    new MutationObserver(sync).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
    // Una classe di lock potrebbe essere gia' presente al primo giro.
    sync();
  }

  if (document.body) avvia();
  else document.addEventListener('DOMContentLoaded', avvia, { once: true });
})();
